import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  listCompanyTemplates,
  updateCompanyTemplate,
  getEmailCadence,
  saveEmailCadence,
} from '../../../../services/emailDomainService';
import CadenceCard from './CadenceCard';
import { Peldano, Repeticiones } from './TemplateCard';

/**
 * CUÁNDO sale el correo de una empresa: la cadencia (días y hora) y el orden de
 * las plantillas en la escalera, con cuántas veces se manda cada una.
 *
 * Vive en «Configuración», que es donde se montan los flujos de comunicación. Los
 * textos (asunto, cuerpo, botones, marca) están en «Plantillas de correo»: ahí
 * solo se escribe lo que dice cada mensaje.
 *
 * Mismos endpoints que la pantalla de plantillas: lo puede cambiar el dueño de la
 * empresa o un admin (`canManageCompanyEmail`).
 */
const mensajeDeError = err => err.response?.data?.error?.message || 'No se pudo completar la operación';

const EmailSchedule = ({ companyId }) => {
  const [cadencia, setCadencia] = useState(null);
  const [plantillas, setPlantillas] = useState([]);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    if (!companyId) return;
    setCargando(true);
    try {
      const [cad, suyas] = await Promise.all([getEmailCadence(companyId), listCompanyTemplates(companyId)]);
      setCadencia(cad);
      setPlantillas(suyas);
    } catch (err) {
      toast.error(mensajeDeError(err));
    } finally {
      setCargando(false);
    }
  }, [companyId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const accion = async (fn, exito) => {
    try {
      await fn();
      if (exito) toast.success(exito);
      await cargar();
    } catch (err) {
      toast.error(mensajeDeError(err));
    }
  };

  if (cargando) return <p className='text-sm text-slate-500'>Cargando…</p>;

  const ordenadas = [...plantillas].sort((a, b) => (a.escalation_order ?? 99) - (b.escalation_order ?? 99));

  return (
    <div className='space-y-3'>
      {cadencia && (
        <CadenceCard
          key={`${companyId}-${cadencia.updatedAt || 'nueva'}`}
          cadencia={cadencia}
          onGuardar={patch => accion(() => saveEmailCadence(companyId, patch), 'Cadencia guardada')}
        />
      )}

      <div>
        <h3 className='text-sm font-medium text-slate-800'>En qué orden</h3>
        <p className='text-xs text-slate-500 mb-2'>
          El orden en que se contacta en frío, de arriba abajo. «— fuera» = solo se manda a mano. El texto de cada una
          se escribe en Plantillas, pestaña Correo.
        </p>
        {ordenadas.length === 0 ? (
          <p className='text-sm text-slate-500'>Todavía no hay plantillas de correo. Créalas en Plantillas, pestaña Correo.</p>
        ) : (
          <ul className='divide-y divide-slate-100 border border-slate-200 rounded-lg'>
            {ordenadas.map(p => (
              <li key={p.key} className='flex flex-wrap items-center justify-between gap-3 px-3 py-2'>
                <div className='min-w-0'>
                  <p className='text-sm text-slate-800'>{p.description || p.key}</p>
                  <p className='text-xs text-slate-500 truncate'>{p.subject}</p>
                </div>
                <div className='flex items-center gap-3'>
                  <Peldano
                    plantilla={p}
                    onCambiar={orden =>
                      accion(
                        () => updateCompanyTemplate(companyId, p.key, { escalation_order: orden }),
                        orden === null ? 'Fuera de la escalera: ahora solo se manda a mano' : `Pasa a ser el contacto nº ${orden}`
                      )
                    }
                  />
                  {p.escalation_order !== null && (
                    <Repeticiones
                      plantilla={p}
                      onCambiar={veces =>
                        accion(
                          () => updateCompanyTemplate(companyId, p.key, { repeats: veces }),
                          veces === 1 ? 'Se manda una vez' : `Se manda ${veces} veces`
                        )
                      }
                    />
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default EmailSchedule;
